import { useState } from "react"
import type { CSSProperties, ReactNode } from "react"
import { Check, ChevronRight, Info, Search, X } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { T, contrastInk, softVariant } from "./theme"
import type { Stage } from "./theme"

// ─── Touchable ──────────────────────────────────────────────────────────────

interface TouchableProps {
  children: ReactNode
  onClick?: () => void
  style?: CSSProperties
  disabled?: boolean
  activeOpacity?: number
  as?: "div" | "button"
}

export function Touchable({
  children,
  onClick,
  style,
  disabled,
  activeOpacity = 0.6,
  as = "div",
}: TouchableProps) {
  const [pressed, setPressed] = useState(false)
  const Element = as as "div"
  return (
    <Element
      onClick={disabled ? undefined : onClick}
      onPointerDown={() => !disabled && setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : pressed ? activeOpacity : 1,
        transition: "opacity 80ms",
        userSelect: "none",
        WebkitTapHighlightColor: "transparent",
        ...style,
      }}
    >
      {children}
    </Element>
  )
}

// ─── MButton ────────────────────────────────────────────────────────────────

type ButtonVariant = "primary" | "accent" | "ghost" | "subtle" | "danger" | "success"
type ButtonSize = "sm" | "md" | "lg"

interface MButtonProps {
  children: ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  onClick?: () => void
  disabled?: boolean
  style?: CSSProperties
  leftIcon?: LucideIcon
  rightIcon?: LucideIcon
  fullWidth?: boolean
}

const BUTTON_SIZES: Record<ButtonSize, { h: number; fs: number; padX: number; gap: number; iconSize: number }> = {
  sm: { h: 36, fs: 13, padX: 14, gap: 6, iconSize: 14 },
  md: { h: 46, fs: 15, padX: 18, gap: 8, iconSize: 16 },
  lg: { h: 54, fs: 16, padX: 22, gap: 10, iconSize: 18 },
}

const BUTTON_VARIANTS: Record<ButtonVariant, { bg: string; color: string; border: string }> = {
  primary: { bg: T.ink,        color: "#fff",      border: T.ink },
  accent:  { bg: T.accent,     color: T.accentInk, border: T.accent },
  ghost:   { bg: "transparent",color: T.ink,       border: T.border },
  subtle:  { bg: T.surfaceAlt, color: T.ink,       border: T.border },
  danger:  { bg: T.danger,     color: "#fff",      border: T.danger },
  success: { bg: T.success,    color: "#fff",      border: T.success },
}

export function MButton({
  children,
  variant = "primary",
  size = "md",
  onClick,
  disabled,
  style,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  fullWidth,
}: MButtonProps) {
  const sizeCfg = BUTTON_SIZES[size]
  const v = BUTTON_VARIANTS[variant]
  return (
    <Touchable
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: sizeCfg.gap,
        height: sizeCfg.h,
        padding: `0 ${sizeCfg.padX}px`,
        background: v.bg,
        color: v.color,
        border: `1px solid ${v.border}`,
        borderRadius: T.radius,
        fontFamily: T.fontDisplay,
        fontSize: sizeCfg.fs,
        fontWeight: 600,
        letterSpacing: 0.2,
        whiteSpace: "nowrap",
        textTransform: "uppercase",
        width: fullWidth ? "100%" : "auto",
        boxSizing: "border-box",
        ...style,
      }}
    >
      {LeftIcon && <LeftIcon size={sizeCfg.iconSize} color={v.color} />}
      {children}
      {RightIcon && <RightIcon size={sizeCfg.iconSize} color={v.color} />}
    </Touchable>
  )
}

// ─── MStageBadge — driven by a real Stage record (id/name/color) ────────────

interface MStageBadgeProps {
  stage: Stage
  size?: "sm" | "md"
  variant?: "solid" | "soft"
}

export function MStageBadge({ stage, size = "md", variant = "solid" }: MStageBadgeProps) {
  const sm = size === "sm"
  if (variant === "solid") {
    const ink = contrastInk(stage.color)
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          background: stage.color,
          color: ink,
          padding: sm ? "1px 7px" : "3px 9px",
          borderRadius: 0,
          fontFamily: T.fontMono,
          fontSize: sm ? 10.5 : 11.5,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          whiteSpace: "nowrap",
          // White stages need a hairline so they don't disappear on white surfaces
          border: stage.color.toLowerCase() === "#ffffff" ? `1px solid ${T.ink}` : "none",
        }}
      >
        <span style={{ width: 5, height: 5, background: ink, opacity: 0.7 }} />
        {stage.name}
      </span>
    )
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: softVariant(stage.color, 0.16),
        color: stage.color,
        padding: sm ? "1px 7px" : "3px 9px",
        borderRadius: 0,
        border: `1px solid ${softVariant(stage.color, 0.4)}`,
        fontFamily: T.fontMono,
        fontSize: sm ? 10.5 : 11.5,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 5, height: 5, background: stage.color }} />
      {stage.name}
    </span>
  )
}

// ─── SectionLabel ───────────────────────────────────────────────────────────

interface SectionLabelProps {
  children: ReactNode
  action?: ReactNode
  onAction?: () => void
  style?: CSSProperties
}

export function SectionLabel({ children, action, onAction, style }: SectionLabelProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 4px",
        marginBottom: 10,
        ...style,
      }}
    >
      <div
        style={{
          fontFamily: T.fontMono,
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: T.inkSoft,
        }}
      >
        {children}
      </div>
      {action && (
        <Touchable
          onClick={onAction}
          style={{
            fontFamily: T.fontBody,
            fontSize: 13,
            fontWeight: 500,
            color: T.accent,
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {action}
        </Touchable>
      )}
    </div>
  )
}

// ─── MHeader ────────────────────────────────────────────────────────────────

interface MHeaderProps {
  title: string
  subtitle?: string
  leftIcon?: LucideIcon
  onLeftPress?: () => void
  rightIcon?: LucideIcon
  onRightPress?: () => void
  rightExtra?: ReactNode
  transparent?: boolean
}

export function MHeader({
  title,
  subtitle,
  leftIcon: LeftIcon,
  onLeftPress,
  rightIcon: RightIcon,
  onRightPress,
  rightExtra,
  transparent,
}: MHeaderProps) {
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        height: T.headerHeight,
        padding: "0 8px",
        background: transparent ? "transparent" : T.surface,
        borderBottom: transparent ? "none" : `1px solid ${T.borderSoft}`,
        display: "flex",
        alignItems: "center",
        gap: 4,
        backdropFilter: "blur(12px)",
      }}
    >
      {LeftIcon && (
        <Touchable
          onClick={onLeftPress}
          style={{
            width: 44, height: 44, display: "grid", placeItems: "center", borderRadius: 999,
          }}
        >
          <LeftIcon size={22} color={T.ink} />
        </Touchable>
      )}
      <div style={{ flex: 1, minWidth: 0, padding: LeftIcon ? 0 : "0 8px" }}>
        <div
          style={{
            fontFamily: T.fontDisplay,
            fontSize: 17,
            fontWeight: 600,
            color: T.ink,
            letterSpacing: -0.3,
            lineHeight: 1.1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            style={{
              fontFamily: T.fontBody, fontSize: 12, color: T.inkSoft, marginTop: 1,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {rightExtra}
      {RightIcon && (
        <Touchable
          onClick={onRightPress}
          style={{
            width: 44, height: 44, display: "grid", placeItems: "center", borderRadius: 999,
          }}
        >
          <RightIcon size={22} color={T.ink} />
        </Touchable>
      )}
    </header>
  )
}

// ─── BottomSheet ────────────────────────────────────────────────────────────

interface BottomSheetProps {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  /**
   * All bottom sheets in the mobile UI render at the same fixed height so that
   * nested sheets (e.g. a SearchablePicker opened from inside the inventory
   * filter drawer or the move-location form) stack flush over their host. With
   * `height: "auto"` each sheet would size to its content, leaving a tall host
   * sheet's title peeking above a shorter picker. Override only with a clear
   * reason — mismatched heights break the stacking invariant.
   */
  height?: number | string
  maxHeight?: number | string
}

export function BottomSheet({
  open,
  onClose,
  title,
  children,
  height = "85%",
  maxHeight = "85%",
}: BottomSheetProps) {
  if (!open) return null
  // Use position: fixed (not absolute) so a BottomSheet opened from inside
  // another BottomSheet anchors to the viewport instead of the parent sheet's
  // inner panel — otherwise nested sheets render constrained to the host
  // sheet's box and only the bottom strip is visible.
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, animation: "mfadeIn 200ms" }}>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.45)" }}
      />
      <div
        style={{
          position: "fixed",
          left: 0, right: 0, bottom: 0,
          background: T.surface,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          boxShadow: T.shadowSheet,
          animation: "msheetUp 240ms cubic-bezier(.22,1,.36,1)",
          height,
          maxHeight,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "10px 0 0", display: "flex", justifyContent: "center", flexShrink: 0 }}>
          <div style={{ width: 40, height: 4, borderRadius: 999, background: T.border }} />
        </div>
        {title && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px 8px",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                fontFamily: T.fontDisplay,
                fontSize: 17,
                fontWeight: 600,
                color: T.ink,
                letterSpacing: -0.3,
              }}
            >
              {title}
            </div>
            <Touchable
              onClick={onClose}
              style={{
                width: 32, height: 32, display: "grid", placeItems: "center",
                borderRadius: 999, background: T.surfaceAlt,
              }}
            >
              <X size={16} color={T.inkSoft} />
            </Touchable>
          </div>
        )}
        <div style={{ overflow: "auto", flex: 1, WebkitOverflowScrolling: "touch" }}>
          {children}
        </div>
      </div>
    </div>
  )
}

// ─── MCard ──────────────────────────────────────────────────────────────────

interface MCardProps {
  children: ReactNode
  style?: CSSProperties
  padded?: boolean
  onClick?: () => void
}

export function MCard({ children, style, padded = true, onClick }: MCardProps) {
  const baseStyle: CSSProperties = {
    background: T.surface,
    borderRadius: T.radius,
    border: `1px solid ${T.border}`,
    padding: padded ? 16 : 0,
    ...style,
  }
  if (onClick) {
    return <Touchable onClick={onClick} style={baseStyle}>{children}</Touchable>
  }
  return <div style={baseStyle}>{children}</div>
}

// ─── MField ─────────────────────────────────────────────────────────────────

interface MFieldProps {
  label: string
  value?: string | null
  mono?: boolean
  onPress?: () => void
  /**
   * Let a long value wrap onto further lines instead of ellipsizing. For
   * fields shown precisely because the caller's compact view had to truncate
   * them, an ellipsis here would hide the same characters twice.
   */
  wrap?: boolean
}

export function MField({ label, value, mono, onPress, wrap }: MFieldProps) {
  return (
    <Touchable
      onClick={onPress}
      style={{
        padding: "12px 0",
        borderBottom: `1px solid ${T.divider}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontFamily: T.fontMono,
            fontSize: 10,
            color: T.inkSoft,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            marginBottom: 4,
            fontWeight: 600,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontFamily: mono ? T.fontMono : T.fontBody,
            fontSize: 15,
            color: T.ink,
            fontWeight: 500,
            whiteSpace: wrap ? "normal" : "nowrap",
            overflow: wrap ? undefined : "hidden",
            textOverflow: wrap ? undefined : "ellipsis",
            overflowWrap: wrap ? "anywhere" : undefined,
          }}
        >
          {value || <span style={{ color: T.inkMuted }}>—</span>}
        </div>
      </div>
      {onPress && <ChevronRight size={16} color={T.inkMuted} />}
    </Touchable>
  )
}

// ─── MTagChip ───────────────────────────────────────────────────────────────

interface MTagChipProps {
  tag: { id: string | number; name: string; color: string }
}

/** Read-only tag pill, tinted with the account-configured tag colour. */
export function MTagChip({ tag }: MTagChipProps) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        background: softVariant(tag.color, 0.18),
        color: tag.color,
        border: `1px solid ${softVariant(tag.color, 0.35)}`,
        borderRadius: 0,
        fontFamily: T.fontMono,
        fontSize: 11.5,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.6,
      }}
    >
      <span style={{ width: 7, height: 7, background: tag.color }} />
      {tag.name}
    </span>
  )
}

// ─── MChip ──────────────────────────────────────────────────────────────────

interface MChipProps {
  children: ReactNode
  active?: boolean
  onClick?: () => void
  leftDot?: boolean
  color?: string
  removable?: boolean
  onRemove?: () => void
}

export function MChip({
  children,
  active,
  onClick,
  leftDot,
  color,
  removable,
  onRemove,
}: MChipProps) {
  return (
    <Touchable
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        background: active ? T.ink : T.surface,
        color: active ? T.surface : T.ink,
        border: `1px solid ${active ? T.ink : T.border}`,
        borderRadius: T.radius,
        fontFamily: T.fontMono,
        fontSize: 11.5,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        whiteSpace: "nowrap",
      }}
    >
      {leftDot && (
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: 999,
            background: color || (active ? T.surface : T.accent),
          }}
        />
      )}
      {children}
      {removable && (
        <span
          onClick={(e) => {
            e.stopPropagation()
            onRemove?.()
          }}
          style={{ display: "inline-flex", marginLeft: 2, marginRight: -4 }}
        >
          <X size={12} color={active ? T.surface : T.inkSoft} />
        </span>
      )}
    </Touchable>
  )
}

// ─── SearchBar ──────────────────────────────────────────────────────────────

interface SearchBarProps {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  onFocus?: () => void
}

export function SearchBar({ value, onChange, placeholder = "Buscar…", onFocus }: SearchBarProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        height: 44,
        padding: "0 14px",
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
      }}
    >
      <Search size={17} color={T.inkSoft} />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        placeholder={placeholder}
        style={{
          flex: 1,
          border: "none",
          outline: "none",
          background: "transparent",
          fontFamily: T.fontBody,
          // iOS Safari auto-zooms when a focused input is below 16px. Keep at
          // 16 to avoid the zoom-on-tap (and the layout reflow that follows).
          fontSize: 16,
          color: T.ink,
          minWidth: 0,
        }}
      />
      {value && (
        <Touchable
          onClick={() => onChange("")}
          style={{
            display: "grid",
            placeItems: "center",
            width: 22, height: 22, borderRadius: 999, background: T.inkMuted,
          }}
        >
          <X size={12} color="#fff" />
        </Touchable>
      )}
    </div>
  )
}

// ─── Avatar ─────────────────────────────────────────────────────────────────

interface AvatarProps {
  name?: string | null
  size?: number
  color?: string
}

export function Avatar({ name, size = 32, color = T.accent }: AvatarProps) {
  const initials = (name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase()
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        background: color,
        color: "#fff",
        display: "grid",
        placeItems: "center",
        fontFamily: T.fontBody,
        fontWeight: 700,
        fontSize: size * 0.4,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  )
}

// ─── Toast ──────────────────────────────────────────────────────────────────

interface ToastProps {
  message: ReactNode
  kind?: "success" | "error" | "info"
}

export function Toast({ message, kind = "success" }: ToastProps) {
  const palette = {
    success: { Icon: Check, iconBg: T.success },
    error:   { Icon: X,     iconBg: T.danger },
    info:    { Icon: Info,  iconBg: T.accent },
  }[kind]
  const Icon = palette.Icon
  return (
    <div
      style={{
        position: "absolute",
        left: 16, right: 16, bottom: 90,
        zIndex: 60,
        animation: "mtoastIn 220ms cubic-bezier(.22,1,.36,1)",
      }}
    >
      <div
        style={{
          background: T.ink,
          color: "#fff",
          borderRadius: T.radius,
          padding: "12px 14px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          boxShadow: T.shadowLg,
          borderLeft: `3px solid ${palette.iconBg}`,
        }}
      >
        <span
          style={{
            width: 26, height: 26, borderRadius: 999, background: palette.iconBg,
            display: "grid", placeItems: "center", flexShrink: 0,
          }}
        >
          <Icon size={14} color="#fff" strokeWidth={2.5} />
        </span>
        <span style={{ fontFamily: T.fontBody, fontSize: 14, fontWeight: 500, flex: 1 }}>
          {message}
        </span>
      </div>
    </div>
  )
}

// ─── BrandMark (mobile: blocky monogram + label) ────────────────────────────

export function BrandMark({ size = 36 }: { size?: number }) {
  const { t } = useTranslation()
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div
        style={{
          width: size,
          height: size,
          background: T.ink,
          color: T.accent,
          display: "grid",
          placeItems: "center",
          borderRadius: 0,
          position: "relative",
          fontFamily: T.fontMono,
          fontWeight: 700,
          fontSize: size * 0.45,
          letterSpacing: -0.5,
        }}
      >
        <span style={{ position: "relative", zIndex: 1 }}>bf</span>
        <span style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 4, background: T.accent }} />
      </div>
      <div style={{ lineHeight: 1 }}>
        {/* "BIERTRACK" is the product name and stays untranslated; the tagline below it is localized. */}
        <div
          style={{
            fontFamily: T.fontDisplay,
            fontWeight: 700,
            fontSize: 17,
            letterSpacing: -0.3,
            color: T.ink,
          }}
        >
          BIERTRACK
        </div>
        <div
          style={{
            fontFamily: T.fontMono,
            fontSize: 9.5,
            letterSpacing: 1.5,
            color: T.inkSoft,
            marginTop: 3,
            textTransform: "uppercase",
          }}
        >
          {t("mobile.brand.tagline")}
        </div>
      </div>
    </div>
  )
}
